import { ChangeEvent, useRef } from "react";
import { Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useUploadBusinessLogo } from "@/hooks/useBusinesses";
import { LogoAvatar } from "@/components/shared/LogoAvatar";

interface BusinessLogoUploaderProps {
  businessId?: string;
  currentUrl?: string | null;
  name?: string;
  onUrlChange: (url: string | null) => void;
}

export const BusinessLogoUploader = ({
  businessId,
  currentUrl,
  name,
  onUrlChange,
}: BusinessLogoUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadMut = useUploadBusinessLogo();

  const onSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (businessId) {
      try {
        const res = await uploadMut.mutateAsync({ id: businessId, file });
        onUrlChange(res.logoUrl);
        toast.success("Business logo updated");
      } catch (err: any) {
        toast.error(err?.message ?? "Failed to upload logo");
      } finally {
        if (inputRef.current) inputRef.current.value = "";
      }
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          onUrlChange(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="flex items-center gap-4 py-1">
      <LogoAvatar
        src={currentUrl}
        name={name || "Business"}
        size="xl"
        className="h-20 w-20 text-3xl font-extrabold rounded-xl"
      />
      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium">Business Logo</p>
        <p className="text-xs text-muted-foreground">
          JPEG, PNG, WebP, or SVG format
        </p>
        <div className="flex items-center gap-2 pt-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            onChange={onSelect}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={uploadMut.isPending}
          >
            <Upload className="mr-1.5 h-3.5 w-3.5" />
            {uploadMut.isPending
              ? "Uploading…"
              : currentUrl
                ? "Change logo"
                : "Upload logo"}
          </Button>
          {currentUrl ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => onUrlChange(null)}
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Remove
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
